import React, { useEffect, useMemo, useState } from 'react';
// src/pages/staff/StaffDirectory.tsx
import { UserIcon, PlusIcon, PencilIcon, TrashIcon, MailIcon, SearchIcon, FilterIcon, BookOpenIcon, ClockIcon, PlusCircleIcon, MinusCircleIcon, CalendarIcon, Award, Eye, Info } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import DataTable from '../../components/ui/DataTable';
import Modal from '../../components/ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useAllocation } from '../../contexts/AllocationContext';
import { useGrid } from '../../contexts/GridContext';
import { useToast } from '../../contexts/ToastContext';
import { StaffAllowance, DEFAULT_ALLOWANCE_TEMPLATES, getAllowancesForSemester, allowanceAppliesToYear } from '../../types/staff';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import { useNavigate } from 'react-router-dom';
// Import the AllowanceEditor component
import AllowanceEditor from '../../components/staff/AllowanceEditor';
import { useLearningAreas } from '../../hooks/useLearningAreas';
import { displayLearningArea } from '../../constants/learningAreas';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
// These were local copies of the shared helpers in types/staff.ts, and being
// copies they never picked up year scoping — so once a staff member had both a
// 2026 and a 2027 allowance this page summed both. Deleted in favour of the
// shared, year-aware versions; the thin wrappers below just bind the year.
const calculateTotalAllowanceMinutes = (staffMember: any, semester: 'semester1' | 'semester2', year?: number) => {
  const allAllowances = getAllowancesForSemester(staffMember, semester, year);
  return allAllowances.reduce((sum: number, a: StaffAllowance) => sum + a.minutes, 0);
};
const StaffDirectory: React.FC = () => {
  const navigate = useNavigate();
  const { learningAreaNames } = useLearningAreas();
  const { workingYear } = useAcademicYear();
  // Get data from context with defensive handling
  const dataContext = useData();
  // Extract values directly - they should already be arrays from your DataContext
  const staff = dataContext?.staff || [];
  const departments = dataContext?.departments || [];
  const allowanceTemplates = dataContext?.allowanceTemplates || [];
  // Get other context functions
  const {
    deleteStaffMember,
    addStaffMember,
    updateStaffMember,
    loading: dataLoading
  } = dataContext || {};
  const {
    getTeacherLoad,
    getTeacherSemesterLoad
  } = useAllocation();
  const { activeGrid } = useGrid();
  const {
    addToast
  } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');

  // Get actual curriculum areas from staff data, splitting on "/" delimiter
  const actualCurriculumAreas = useMemo(() => {
    if (!Array.isArray(staff)) return [];
    
    // Helper function to validate if a string is a valid department name
    const isValidDepartment = (dept: string): boolean => {
      // Filter out invalid patterns
      const isInvalid = (
        /^\d+$/.test(dept) ||  // Pure numbers like "1396", "240"
        /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)/i.test(dept) ||  // Day abbreviations and full days
        /Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday/i.test(dept) ||  // Full day names
        /Coordinator|POR|Duke|morning|afternoon/i.test(dept) ||  // Roles/times
        /^\w{1,2}$/.test(dept) ||  // Single/double letters like "A", "VM", "PE", "RE"
        /minutes|taught|level|just|Mondays/i.test(dept) ||  // Schedule related
        dept.length < 3 ||  // Too short
        dept.includes('.') ||  // Contains periods like "Thursday."
        /^(VM|PE|RE|Thurs)$/i.test(dept)  // Known invalid codes and abbreviations
      );
      
      // Allow "LEARNING SUPPORT" even though it contains spaces
      if (dept === 'LEARNING SUPPORT') {
        return true;
      }
      
      return !isInvalid;
    };
    
    const areas = new Set<string>();
    staff.forEach(member => {
      // Get department from staff member, excluding "General"
      if (member.department && member.department !== 'General') {
        // Split on "/" and trim each part
        const departmentParts = member.department.split('/').map(part => part.trim()).filter(part => part && isValidDepartment(part));
        departmentParts.forEach(part => areas.add(part));
      }
      // Also check curriculumArea field if it exists
      if (member.curriculumArea && member.curriculumArea !== 'General') {
        const areaParts = member.curriculumArea.split('/').map(part => part.trim()).filter(part => part && isValidDepartment(part));
        areaParts.forEach(part => areas.add(part));
      }
    });
    
    return Array.from(areas).sort();
  }, [staff]);

  // Fallback curriculum areas for when creating new staff (curriculum leader assignment)
  const allCurriculumAreas = [
    'English',
    'Mathematics',
    'Science',
    'Humanities',
    'Arts',
    'Music',
    'HPE',
    'Technology',
    'Languages',
    'Business',
    'Religion',
    'Agriculture',
    'VM/VET',
    'Focus',
    'Other'
  ];
  
  // Learning-area options come from the canonical list; keep any currently
  // selected filter value that isn't canonical so it stays available.
  const departmentOptionNames = filterDepartment !== 'all' && !learningAreaNames.includes(filterDepartment)
    ? [...learningAreaNames, filterDepartment]
    : learningAreaNames;
  const departmentOptions = departmentOptionNames
    .filter(area => area)
    .map((area, index) => ({ id: `${(area || '').toLowerCase()}-${index}`, name: area, color: '#gray', classCount: 0 }));
  
  const filterOptions = departmentOptions;

  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAllowancesModal, setShowAllowancesModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<any | null>(null);
  const [activeSemester, setActiveSemester] = useState<'semester1' | 'semester2'>('semester1');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    departments: [] as string[], // Changed to array
    subjects: '',
    fte: 1.0,
    status: 'active' as 'active' | 'leave' | 'inactive',
    initial: '',
    role: 'teacher' as 'admin' | 'principal' | 'assistant_principal' | 'head_of_department' | 'curriculum_leader' | 'teacher',
    curriculumArea: '',
    semester1Allowances: [] as StaffAllowance[],
    semester2Allowances: [] as StaffAllowance[],
    bothAllowances: [] as StaffAllowance[]
  });
  // Auto-generate staff code function
  const generateStaffCode = (name: string): string => {
    if (!name.trim()) return '';
    const nameParts = name.trim().split(' ');
    if (nameParts.length >= 2) {
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      return (firstName[0] + lastName.substring(0, 2)).toUpperCase();
    } else {
      return name.substring(0, 3).toUpperCase();
    }
  };
  // Filter staff based on search and filters
  const filteredStaff = useMemo(() => {
    // Extra safety check
    if (!Array.isArray(staff)) {
      console.error('staff is not an array in filteredStaff calculation');
      return [];
    }
    return staff.filter(member => {
      // Search filter
      if (searchQuery && !member.name.toLowerCase().includes(searchQuery.toLowerCase()) && !member.email.toLowerCase().includes(searchQuery.toLowerCase()) && !(member.staffCode && member.staffCode.toLowerCase().includes(searchQuery.toLowerCase()))) {
        return false;
      }
      // Department filter - check if member belongs to the filtered department
      if (filterDepartment !== 'all') {
        const memberDepartments = member.department 
          ? member.department.split('/').map(dept => dept.trim()).filter(dept => dept)
          : [];
        if (!memberDepartments.includes(filterDepartment)) {
          return false;
        }
      }
      // Status filter
      if (filterStatus !== 'all' && member.status !== filterStatus) {
        return false;
      }
      return true;
    });
  }, [staff, searchQuery, filterDepartment, filterStatus]);
  // Handle opening edit modal
  const handleEdit = (staffMember: any) => {
    setSelectedStaff(staffMember);

    // Split departments on "/" and clean up
    const memberDepartments = staffMember.department
      ? staffMember.department.split('/').map((dept: string) => dept.trim()).filter((dept: string) => dept)
      : [];

    setFormData({
      name: staffMember.name,
      email: staffMember.email,
      departments: memberDepartments,
      subjects: (staffMember.subjects || []).join(', '),
      fte: staffMember.fte,
      status: staffMember.status,
      initial: staffMember.staffCode || '',
      role: staffMember.role || 'teacher',
      curriculumArea: staffMember.curriculumArea || '',
      // This year's allowances only. Loading them unfiltered showed each one
      // twice — a 2026 and a 2027 copy, identical and unlabelled — which reads as
      // a duplication bug and invites someone to "tidy up" a year (issue #245).
      // The other years are preserved on save, not rewritten from this form.
      semester1Allowances: (staffMember.semester1?.allowances || []).filter((a: StaffAllowance) => allowanceAppliesToYear(a, workingYear)),
      semester2Allowances: (staffMember.semester2?.allowances || []).filter((a: StaffAllowance) => allowanceAppliesToYear(a, workingYear)),
      bothAllowances: (staffMember.both?.allowances || []).filter((a: StaffAllowance) => allowanceAppliesToYear(a, workingYear))
    });
    setShowEditModal(true);
  };
  // Handle opening delete modal
  const handleDelete = (staffMember: any) => {
    setSelectedStaff(staffMember);
    setShowDeleteModal(true);
  };
  // Handle opening allowances modal
  const handleViewAllowances = (staffMember: any) => {
    setSelectedStaff(staffMember);
    setShowAllowancesModal(true);
  };
  // Handle opening add modal
  const handleAdd = () => {
    setFormData({
      name: '',
      email: '',
      departments: [],
      subjects: '',
      fte: 1.0,
      status: 'active',
      initial: '',
      role: 'teacher',
      curriculumArea: '',
      semester1Allowances: [],
      semester2Allowances: [],
      bothAllowances: []
    });
    setShowAddModal(true);
  };
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };
  // Handle name change with auto-generation of staff code
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setFormData(prev => ({
      ...prev,
      name: newName,
      initial: prev.staffCode || generateStaffCode(newName)
    }));
  };
  // Handle department multi-select changes
  const handleDepartmentChange = (selectedDepartments: string[]) => {
    setFormData(prev => ({
      ...prev,
      departments: selectedDepartments
    }));
  };

  // Handle allowance changes
  const handleAllowancesChange = (semester: 'semester1' | 'semester2', allowances: StaffAllowance[]) => {
    // Separate semester-specific allowances from "both" allowances
    // This prevents duplicates when we merge them for display
    const semesterSpecific = allowances.filter(a => a.appliesTo !== 'both');
    const bothAllowances = allowances.filter(a => a.appliesTo === 'both');

    if (semester === 'semester1') {
      setFormData(prev => ({
        ...prev,
        semester1Allowances: semesterSpecific,
        bothAllowances: bothAllowances // Update both allowances too
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        semester2Allowances: semesterSpecific,
        bothAllowances: bothAllowances // Update both allowances too
      }));
    }
  };
  // Handle saving new staff member
  const handleSaveNew = async () => {
    try {
      if (!formData.initial.trim()) {
        addToast('Staff code is required', 'error');
        return;
      }
      const existingStaff = staff.find(s => s.staffCode === formData.initial.toUpperCase());
      if (existingStaff) {
        addToast(`Staff code "${formData.initial}" is already in use by ${existingStaff.name}`, 'error');
        return;
      }
      // Use system-wide baseMinutesPerFortnight (for now hardcoded to 2220, would come from settings in real implementation)
      const systemBaseMinutesPerFortnight = 2220;
      const semester1 = {
        allowances: formData.semester1Allowances,
        baseMinutesPerFortnight: systemBaseMinutesPerFortnight,
        allocatedMinutes: 0,
        availableMinutes: Math.round(systemBaseMinutesPerFortnight * formData.fte),
        utilization: 0
      };
      const semester2 = {
        allowances: formData.semester2Allowances,
        baseMinutesPerFortnight: systemBaseMinutesPerFortnight,
        allocatedMinutes: 0,
        availableMinutes: Math.round(systemBaseMinutesPerFortnight * formData.fte),
        utilization: 0
      };
      const both = {
        allowances: formData.bothAllowances || []
      };
      await addStaffMember({
        name: formData.name,
        email: formData.email,
        department: formData.departments.join(' / '), // Join with " / "
        subjects: formData.subjects.split(',').map(s => s.trim()).filter(s => s),
        baseMinutesPerFortnight: systemBaseMinutesPerFortnight,
        fte: formData.fte,
        status: formData.status,
        initial: formData.initial.toUpperCase(),
        role: formData.role,
        curriculumArea: formData.curriculumArea,
        semester1,
        semester2,
        both
      });
      setShowAddModal(false);
      addToast('Staff member added successfully', 'success');
    } catch (err) {
      console.error('Error adding staff member:', err);
      if (err instanceof Error) {
        addToast(`Failed to add staff member: ${err.message}`, 'error');
      } else {
        addToast('Failed to add staff member', 'error');
      }
    }
  };
  // Handle updating staff member
  const handleUpdate = async () => {
    if (!selectedStaff) return;
    try {
      if (!formData.initial.trim()) {
        addToast('Staff code is required', 'error');
        return;
      }
      const existingStaff = staff.find(s => s.staffCode === formData.initial.toUpperCase() && s.id !== selectedStaff.id);
      if (existingStaff) {
        addToast(`Staff code "${formData.initial}" is already in use by ${existingStaff.name}`, 'error');
        return;
      }
      // Preserve the staff member's OWN base. This used to assign a hardcoded
      // 2220 to whoever was edited, silently discarding a reduced base (issue
      // #245). Only the demo accounts carry a non-default value today, so nothing
      // real has been lost yet — but it would wipe the field for the first
      // person given one.
      const baseMinutes =
        selectedStaff.baseMinutesPerFortnight || DEFAULT_BASE_MINUTES_PER_FORTNIGHT;

      // The form holds THIS YEAR's allowances only. Everything belonging to other
      // years has to be carried through untouched, or saving here would delete it.
      const keepOtherYears = (existing: StaffAllowance[] | undefined) =>
        (existing || []).filter(a => !allowanceAppliesToYear(a, workingYear));

      const semester1 = {
        ...selectedStaff.semester1,
        allowances: [
          ...keepOtherYears(selectedStaff.semester1?.allowances),
          ...formData.semester1Allowances
        ],
        baseMinutesPerFortnight: baseMinutes,
        availableMinutes: Math.round(baseMinutes * formData.fte)
      };
      const semester2 = {
        ...selectedStaff.semester2,
        allowances: [
          ...keepOtherYears(selectedStaff.semester2?.allowances),
          ...formData.semester2Allowances
        ],
        baseMinutesPerFortnight: baseMinutes,
        availableMinutes: Math.round(baseMinutes * formData.fte)
      };
      const both = {
        ...selectedStaff.both,
        allowances: [
          ...keepOtherYears(selectedStaff.both?.allowances),
          ...(formData.bothAllowances || [])
        ]
      };
      // Send only what actually changed.
      //
      // This used to send every field on every save, which makes the modal a
      // last-write-wins clobber: whatever the form loaded when it opened gets
      // written back over anything changed elsewhere in the meantime. Apply an
      // FTE in the Intention Review panel, then save a staff edit whose form was
      // opened beforehand, and the old FTE lands back on the record — looking
      // exactly like "my change didn't save".
      //
      // Same failure class as #218, where whole-array grid writes destroyed 212
      // assignments. A form is a snapshot, not a statement of intent about every
      // field it happens to contain.
      const candidate: Record<string, unknown> = {
        name: formData.name,
        email: formData.email,
        department: formData.departments.join(' / '), // Join with " / "
        subjects: formData.subjects.split(',').map(s => s.trim()).filter(s => s),
        baseMinutesPerFortnight: baseMinutes,
        fte: formData.fte,
        status: formData.status,
        initial: formData.initial.toUpperCase(),
        role: formData.role,
        curriculumArea: formData.curriculumArea,
        semester1,
        semester2,
        both
      };

      // Compare against the row this form was populated from. Objects and arrays
      // go by JSON shape; scalars by value, with numerics normalised so 0.9 does
      // not read as different from "0.90".
      const unchanged = (a: unknown, b: unknown): boolean => {
        if (a === b) return true;
        if (typeof a === 'object' || typeof b === 'object') {
          return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
        }
        const na = Number(a);
        const nb = Number(b);
        if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
        return String(a ?? '') === String(b ?? '');
      };

      const originals: Record<string, unknown> = {
        name: selectedStaff.name,
        email: selectedStaff.email,
        department: selectedStaff.department,
        subjects: selectedStaff.subjects,
        baseMinutesPerFortnight: selectedStaff.baseMinutesPerFortnight,
        fte: selectedStaff.fte,
        status: selectedStaff.status,
        initial: selectedStaff.staffCode,
        role: selectedStaff.role,
        curriculumArea: selectedStaff.curriculumArea,
        semester1: selectedStaff.semester1,
        semester2: selectedStaff.semester2,
        both: selectedStaff.both
      };

      const changes = Object.fromEntries(
        Object.entries(candidate).filter(([k, v]) => !unchanged(v, originals[k]))
      );

      if (Object.keys(changes).length === 0) {
        setShowEditModal(false);
        addToast('No changes to save', 'info');
        return;
      }

      await updateStaffMember(selectedStaff.id, changes as any);
      setShowEditModal(false);
      addToast('Staff member updated successfully', 'success');
    } catch (err) {
      if (err instanceof Error) {
        addToast(`Failed to update staff member: ${err.message}`, 'error');
      } else {
        addToast('Failed to update staff member', 'error');
      }
    }
  };
  // Handle deleting staff member
  const handleConfirmDelete = async () => {
    if (!selectedStaff) return;
    try {
      await deleteStaffMember(selectedStaff.id);
      setShowDeleteModal(false);
      addToast('Staff member deleted successfully', 'success');
    } catch (err) {
      if (err instanceof Error) {
        addToast(`Failed to delete staff member: ${err.message}`, 'error');
      } else {
        addToast('Failed to delete staff member', 'error');
      }
    }
  };
  // Early return if context is not working
  if (!dataContext) {
    return <div>
        <PageHeader title="Staff Directory" description="Error loading staff data" />
        <Card>
          <div className="p-8 text-center">
            <div className="text-danger-fg">
              <h3 className="text-card-title">Context Error</h3>
              <p className="mt-2">
                Unable to load data context. Please check the DataProvider
                setup.
              </p>
            </div>
          </div>
        </Card>
      </div>;
  }
  // Early return if data is still loading
  if (dataLoading) {
    return <div>
        <PageHeader title="Staff Directory" description="Loading staff data..." />
        <Card>
          <div className="p-8 flex justify-center">
            <Spinner size="lg" label="Loading staff directory…" />
          </div>
        </Card>
      </div>;
  }
  // Define columns for staff table
  const staffColumns = [{
    key: 'name',
    header: 'Name',
    cell: (row: any) => <div className="flex items-center">
          <div className="h-10 w-10 flex-shrink-0 mr-3">
            <div className="h-10 w-10 rounded-full bg-navy-100 flex items-center justify-center">
              <span className="text-navy-600 font-medium">
                {row.name?.split(' ').map((n: string) => n[0]).join('') || '??'}
              </span>
            </div>
          </div>
          <div>
            <div className="font-medium text-ink-900">{row.name}</div>
            <div className="text-ink-500 text-sm flex items-center">
              <MailIcon className="h-3 w-3 mr-1" />
              {row.email}
            </div>
          </div>
        </div>,
    sortable: true,
    filterable: true
  }, {
    key: 'initial',
    header: 'Code',
    cell: (row: any) => <span className="font-mono tabular-nums text-sm bg-navy-100 text-navy-800 px-2 py-1 rounded">
          {row.staffCode || 'N/A'}
        </span>,
    sortable: true
  }, {
    key: 'curriculumArea',
    header: 'Learning Area',
    cell: (row: any) => {
      const departments = row.department 
        ? row.department.split('/').map((dept: string) => dept.trim()).filter((dept: string) => dept)
        : [];
      
      if (departments.length === 0) return 'N/A';
      
      return (
        <div className="flex flex-wrap gap-1">
          {departments.map((dept: string, index: number) => (
            <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-navy-100 text-navy-800">
              {displayLearningArea(dept)}
            </span>
          ))}
        </div>
      );
    },
    sortable: true,
    filterable: true
  }, {
    key: 'subjects',
    header: 'Subjects',
    cell: (row: any) => <div className="flex flex-wrap gap-1">
          {(row.subjects || []).map((subject: string, index: number) => <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-navy-100 text-navy-800">
              {subject}
            </span>)}
        </div>
  }, {
    key: 'load',
    header: 'Teaching Load',
    cell: (row: any) => {
      const semester1Load = getTeacherSemesterLoad(row.id || row.staffCode, 'semester1');
      const semester2Load = getTeacherSemesterLoad(row.id || row.staffCode, 'semester2');
      return <div className="space-y-2">
            {/* Semester 1 */}
            <div>
              <div className="flex items-center mb-1">
                <div className={`h-2 w-16 rounded-full ${semester1Load.overloaded ? 'bg-danger-bg' : semester1Load.utilization >= 100 ? 'bg-ok-bg' : 'bg-ink-200'}`}>
                  <div className={`h-2 rounded-full ${semester1Load.overloaded ? 'bg-danger-solid' : 'bg-ok-solid'}`} style={{
                width: `${Math.min(100, semester1Load.allocatedMinutes / semester1Load.availableMinutes * 100)}%`
              }}></div>
                </div>
                <span className="ml-2 text-sm font-mono tabular-nums">
                  {semester1Load.allocatedMinutes}/
                  {semester1Load.availableMinutes}
                </span>
              </div>
              <div className="text-xs text-ink-500 font-mono tabular-nums">
                S1 • {semester1Load.utilization}% utilised
              </div>
            </div>
            {/* Semester 2 */}
            <div>
              <div className="flex items-center mb-1">
                <div className={`h-2 w-16 rounded-full ${semester2Load.overloaded ? 'bg-danger-bg' : semester2Load.utilization >= 100 ? 'bg-ok-bg' : 'bg-ink-200'}`}>
                  <div className={`h-2 rounded-full ${semester2Load.overloaded ? 'bg-danger-solid' : 'bg-ok-solid'}`} style={{
                width: `${Math.min(100, semester2Load.allocatedMinutes / semester2Load.availableMinutes * 100)}%`
              }}></div>
                </div>
                <span className="ml-2 text-sm font-mono tabular-nums">
                  {semester2Load.allocatedMinutes}/
                  {semester2Load.availableMinutes}
                </span>
              </div>
              <div className="text-xs text-ink-500 font-mono tabular-nums">
                S2 • {semester2Load.utilization}% utilised
              </div>
            </div>
          </div>;
    },
    sortable: true
  }, {
    key: 'allowances',
    header: 'Allowances',
    cell: (row: any) => {
      const totalAllowanceMinutes = calculateTotalAllowanceMinutes(row, activeSemester, workingYear);
      const allAllowances = getAllowancesForSemester(row, activeSemester, workingYear);
      const hasAllowances = allAllowances.length > 0;
      return <div className="flex items-center space-x-2">
            <div>
              <div className="text-sm font-mono tabular-nums">{totalAllowanceMinutes} minutes</div>
              <div className="text-xs text-ink-500 font-mono tabular-nums">
                {allAllowances.length} allowances
              </div>
            </div>
            {hasAllowances && <button onClick={e => {
          e.stopPropagation();
          handleViewAllowances(row);
        }} className="text-navy-600 hover:text-navy-800 p-1 rounded-full hover:bg-navy-50" title="View allowances">
                <Eye className="h-4 w-4" />
              </button>}
          </div>;
    }
  }, {
    key: 'fte',
    header: 'FTE',
    cell: (row: any) => <span className="font-mono tabular-nums">{(row.fte || 0).toFixed(2)}</span>,
    sortable: true
  }, {
    key: 'status',
    header: 'Status',
    cell: (row: any) => {
      if (row.status === 'active') {
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-ok-bg text-ok-fg">
              Active
            </span>;
      }
      if (row.status === 'leave') {
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-warn-bg text-warn-fg">
              On Leave
            </span>;
      }
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-ink-100 text-ink-900">
            Inactive
          </span>;
    },
    sortable: true
  }, {
    key: 'actions',
    header: 'Actions',
    cell: (row: any) => <div className="flex space-x-2">
          <button onClick={e => {
        e.stopPropagation();
        handleEdit(row);
      }} className="text-navy-600 hover:text-navy-800">
            <PencilIcon className="h-4 w-4" />
          </button>
          <button onClick={e => {
        e.stopPropagation();
        handleDelete(row);
      }} className="text-danger-fg hover:text-danger-fg">
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
  }];
  return <div>
      <PageHeader title="Staff Directory" description="Manage staff members and their teaching assignments" actions={<div className="flex space-x-2">
            <Button variant="secondary" leftIcon={<Award className="h-4 w-4" />} onClick={() => navigate('/staff/allowances')}>
              Manage Allowances
            </Button>
            <Button variant="primary" leftIcon={<PlusIcon className="h-4 w-4" />} onClick={handleAdd}>
              Add Staff Member
            </Button>
          </div>} />
      <Card>
        <div className="p-4 border-b border-ink-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-card-title text-ink-900">Staff Members</h3>
            <div className="mt-2 sm:mt-0 flex flex-wrap gap-2">
              <select className="block pl-3 pr-10 py-2 text-sm border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 rounded-md" value={filterDepartment} onChange={e => setFilterDepartment(e.target.value)}>
                <option value="all">All Learning Areas</option>
                {filterOptions.map(option => <option key={option.id} value={option.name}>
                    {option.name}
                  </option>)}
              </select>
              <select className="block pl-3 pr-10 py-2 text-sm border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 rounded-md" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="leave">On Leave</option>
                <option value="inactive">Inactive</option>
              </select>
              <select className="block pl-3 pr-10 py-2 text-sm border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 rounded-md" value={activeSemester} onChange={e => setActiveSemester(e.target.value as 'semester1' | 'semester2')}>
                <option value="semester1">Semester 1</option>
                <option value="semester2">Semester 2</option>
              </select>
            </div>
          </div>
          <div className="mt-4 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon className="h-5 w-5 text-ink-400" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-ink-300 rounded-md leading-5 bg-white placeholder-ink-500 focus:outline-none focus:placeholder-ink-400 focus:ring-1 focus:ring-navy-500 focus:border-navy-500 sm:text-sm" placeholder="Search staff by name, email, or code..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </div>
        </div>
        <DataTable data={filteredStaff} columns={staffColumns} keyField="id" pagination={true} pageSize={10} loading={dataLoading} emptyState={<EmptyState icon={<UserIcon className="h-6 w-6" />} title="No Staff Found" description="Try adjusting your search or filter criteria." action={<Button variant="primary" leftIcon={<PlusIcon className="h-4 w-4" />} onClick={handleAdd}>
                Add Staff Member
              </Button>} />} />
      </Card>
      {/* Add Staff Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add Staff Member" footer={<>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveNew}>
              Add Staff Member
            </Button>
          </>}>
        <div className="space-y-4">
          <div>
            <label htmlFor="add-name" className="block text-sm font-medium text-ink-700">
              Name *
            </label>
            <input type="text" id="add-name" name="name" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.name} onChange={handleNameChange} required />
          </div>
          <div>
            <label htmlFor="add-initial" className="block text-sm font-medium text-ink-700">
              Staff Code *
            </label>
            <input type="text" id="add-initial" name="initial" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.initial} onChange={handleInputChange} maxLength={3} required />
            <p className="mt-1 text-xs text-ink-500">
              3-letter staff code (e.g., ABC)
            </p>
          </div>
          <div>
            <label htmlFor="add-email" className="block text-sm font-medium text-ink-700">
              Email *
            </label>
            <input type="email" id="add-email" name="email" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.email} onChange={handleInputChange} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-700">
              Learning Areas *
            </label>
            <div className="mt-1 border border-ink-300 rounded-md shadow-sm p-2 bg-white min-h-[42px] max-h-32 overflow-y-auto">
              {/* Selected departments */}
              <div className="flex flex-wrap gap-1 mb-2">
                {formData.departments.map((dept, index) => (
                  <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-navy-100 text-navy-800">
                    {dept}
                    <button
                      type="button"
                      onClick={() => handleDepartmentChange(formData.departments.filter((_, i) => i !== index))}
                      className="ml-1 text-navy-600 hover:text-navy-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              {/* Available departments */}
              <div className="flex flex-wrap gap-1">
                {departmentOptions
                  .filter(option => !formData.departments.includes(option.name))
                  .map(option => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleDepartmentChange([...formData.departments, option.name])}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-ink-100 text-ink-700 hover:bg-ink-200"
                    >
                      + {option.name}
                    </button>
                  ))}
              </div>
            </div>
            <p className="mt-1 text-xs text-ink-500">
              Select multiple learning areas. Staff will appear in all selected learning areas.
            </p>
          </div>
          <div>
            <label htmlFor="add-subjects" className="block text-sm font-medium text-ink-700">
              Subjects (comma-separated)
            </label>
            <textarea id="add-subjects" name="subjects" rows={2} className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.subjects} onChange={handleInputChange} placeholder="e.g., VCE English 1/2, Year 9 English, Year 10 Literature" />
          </div>
          <div>
            <label htmlFor="add-fte" className="block text-sm font-medium text-ink-700">
              FTE (Full Time Equivalent) *
            </label>
            <input type="number" id="add-fte" name="fte" min="0" max="1" step="0.1" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.fte} onChange={handleInputChange} required />
            <p className="mt-1 text-xs text-ink-500">
              1.0 = Full time (2220 minutes), 0.5 = Half time (1110 minutes)
            </p>
          </div>
          <div>
            <label htmlFor="add-status" className="block text-sm font-medium text-ink-700">
              Status *
            </label>
            <select id="add-status" name="status" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.status} onChange={handleInputChange} required>
              <option value="active">Active</option>
              <option value="leave">On Leave</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label htmlFor="add-role" className="block text-sm font-medium text-ink-700">
              Role *
            </label>
            <select id="add-role" name="role" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.role} onChange={handleInputChange} required>
              <option value="teacher">Teacher</option>
              <option value="curriculum_leader">Curriculum Leader</option>
              <option value="head_of_department">Head of Department</option>
              <option value="assistant_principal">Assistant Principal</option>
              <option value="principal">Principal</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {formData.role === 'curriculum_leader' && (
            <div>
              <label htmlFor="add-curriculumArea" className="block text-sm font-medium text-ink-700">
                Learning Area *
              </label>
              <select id="add-curriculumArea" name="curriculumArea" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.curriculumArea} onChange={handleInputChange} required>
                <option value="">Select Learning Area</option>
                {(formData.curriculumArea && !learningAreaNames.includes(formData.curriculumArea)
                  ? [...learningAreaNames, formData.curriculumArea]
                  : learningAreaNames).map(area => (
                  <option key={area} value={area}>
                    {area}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-ink-700 mb-3">
              Staff Allowances
            </h4>
            <div className="flex space-x-2 mb-4">
              <button type="button" className={`px-3 py-1 text-sm rounded ${activeSemester === 'semester1' ? 'bg-navy-100 text-navy-800' : 'bg-ink-100'}`} onClick={() => setActiveSemester('semester1')}>
                Semester 1
              </button>
              <button type="button" className={`px-3 py-1 text-sm rounded ${activeSemester === 'semester2' ? 'bg-navy-100 text-navy-800' : 'bg-ink-100'}`} onClick={() => setActiveSemester('semester2')}>
                Semester 2
              </button>
            </div>
            {activeSemester === 'semester1' ? <AllowanceEditor allowances={[...formData.semester1Allowances, ...(formData.bothAllowances || [])]} semester="semester1" onChange={allowances => handleAllowancesChange('semester1', allowances)} availableTemplates={allowanceTemplates} year={workingYear} /> : <AllowanceEditor allowances={[...formData.semester2Allowances, ...(formData.bothAllowances || [])]} semester="semester2" onChange={allowances => handleAllowancesChange('semester2', allowances)} availableTemplates={allowanceTemplates} year={workingYear} />}
          </div>
        </div>
      </Modal>
      {/* Edit Staff Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title={`Edit Staff: ${selectedStaff?.name}`} footer={<>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdate}>
              Update Staff Member
            </Button>
          </>}>
        <div className="space-y-4">
          <div>
            <label htmlFor="edit-name" className="block text-sm font-medium text-ink-700">
              Name *
            </label>
            <input type="text" id="edit-name" name="name" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.name} onChange={handleNameChange} required />
          </div>
          <div>
            <label htmlFor="edit-initial" className="block text-sm font-medium text-ink-700">
              Staff Code *
            </label>
            <input type="text" id="edit-initial" name="initial" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.initial} onChange={handleInputChange} maxLength={3} required />
            <p className="mt-1 text-xs text-ink-500">
              3-letter staff code (e.g., ABC)
            </p>
          </div>
          <div>
            <label htmlFor="edit-email" className="block text-sm font-medium text-ink-700">
              Email *
            </label>
            <input type="email" id="edit-email" name="email" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.email} onChange={handleInputChange} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-700">
              Learning Areas *
            </label>
            <div className="mt-1 border border-ink-300 rounded-md shadow-sm p-2 bg-white min-h-[42px] max-h-32 overflow-y-auto">
              {/* Selected departments */}
              <div className="flex flex-wrap gap-1 mb-2">
                {formData.departments.map((dept, index) => (
                  <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-navy-100 text-navy-800">
                    {dept}
                    <button
                      type="button"
                      onClick={() => handleDepartmentChange(formData.departments.filter((_, i) => i !== index))}
                      className="ml-1 text-navy-600 hover:text-navy-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              {/* Available departments */}
              <div className="flex flex-wrap gap-1">
                {departmentOptions
                  .filter(option => !formData.departments.includes(option.name))
                  .map(option => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleDepartmentChange([...formData.departments, option.name])}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-ink-100 text-ink-700 hover:bg-ink-200"
                    >
                      + {option.name}
                    </button>
                  ))}
              </div>
            </div>
            <p className="mt-1 text-xs text-ink-500">
              Select multiple learning areas. Staff will appear in all selected learning areas.
            </p>
          </div>
          <div>
            <label htmlFor="edit-subjects" className="block text-sm font-medium text-ink-700">
              Subjects (comma-separated)
            </label>
            <textarea id="edit-subjects" name="subjects" rows={2} className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.subjects} onChange={handleInputChange} placeholder="e.g., VCE English 1/2, Year 9 English, Year 10 Literature" />
          </div>
          <div>
            <label htmlFor="edit-fte" className="block text-sm font-medium text-ink-700">
              FTE (Full Time Equivalent) *
            </label>
            <input type="number" id="edit-fte" name="fte" min="0" max="1" step="0.1" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.fte} onChange={handleInputChange} required />
            <p className="mt-1 text-xs text-ink-500">
              1.0 = Full time (2220 minutes), 0.5 = Half time (1110 minutes)
            </p>
          </div>
          <div>
            <label htmlFor="edit-status" className="block text-sm font-medium text-ink-700">
              Status *
            </label>
            <select id="edit-status" name="status" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.status} onChange={handleInputChange} required>
              <option value="active">Active</option>
              <option value="leave">On Leave</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label htmlFor="edit-role" className="block text-sm font-medium text-ink-700">
              Role *
            </label>
            <select id="edit-role" name="role" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.role} onChange={handleInputChange} required>
              <option value="teacher">Teacher</option>
              <option value="curriculum_leader">Curriculum Leader</option>
              <option value="head_of_department">Head of Department</option>
              <option value="assistant_principal">Assistant Principal</option>
              <option value="principal">Principal</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {formData.role === 'curriculum_leader' && (
            <div>
              <label htmlFor="edit-curriculumArea" className="block text-sm font-medium text-ink-700">
                Learning Area *
              </label>
              <select id="edit-curriculumArea" name="curriculumArea" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.curriculumArea} onChange={handleInputChange} required>
                <option value="">Select Learning Area</option>
                {(formData.curriculumArea && !learningAreaNames.includes(formData.curriculumArea)
                  ? [...learningAreaNames, formData.curriculumArea]
                  : learningAreaNames).map(area => (
                  <option key={area} value={area}>
                    {area}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-ink-700 mb-3">
              Staff Allowances
            </h4>
            <div className="flex space-x-2 mb-4">
              <button type="button" className={`px-3 py-1 text-sm rounded ${activeSemester === 'semester1' ? 'bg-navy-100 text-navy-800' : 'bg-ink-100'}`} onClick={() => setActiveSemester('semester1')}>
                Semester 1
              </button>
              <button type="button" className={`px-3 py-1 text-sm rounded ${activeSemester === 'semester2' ? 'bg-navy-100 text-navy-800' : 'bg-ink-100'}`} onClick={() => setActiveSemester('semester2')}>
                Semester 2
              </button>
            </div>
            {activeSemester === 'semester1' ? <AllowanceEditor allowances={[...formData.semester1Allowances, ...(formData.bothAllowances || [])]} semester="semester1" onChange={allowances => handleAllowancesChange('semester1', allowances)} availableTemplates={allowanceTemplates} year={workingYear} /> : <AllowanceEditor allowances={[...formData.semester2Allowances, ...(formData.bothAllowances || [])]} semester="semester2" onChange={allowances => handleAllowancesChange('semester2', allowances)} availableTemplates={allowanceTemplates} year={workingYear} />}
          </div>
        </div>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Staff Member" footer={<>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </>}>
        <div>
          <p className="text-sm text-ink-500">
            Are you sure you want to delete {selectedStaff?.name}? This action
            cannot be undone.
          </p>
          <p className="mt-2 text-sm text-danger-fg">
            Note: Staff members assigned to classes cannot be deleted. You must
            first remove all class assignments.
          </p>
        </div>
      </Modal>
      {/* View Allowances Modal */}
      <Modal isOpen={showAllowancesModal} onClose={() => setShowAllowancesModal(false)} title={`Allowances for ${selectedStaff?.name}`} footer={<>
            <Button variant="secondary" onClick={() => setShowAllowancesModal(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={() => {
        setShowAllowancesModal(false);
        handleEdit(selectedStaff);
      }}>
              Edit Allowances
            </Button>
          </>}>
        {selectedStaff && <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 rounded-full bg-navy-100 flex items-center justify-center">
                <span className="text-navy-600 font-medium">
                  {selectedStaff.name?.split(' ').map((n: string) => n[0]).join('') || '??'}
                </span>
              </div>
              <div>
                <h3 className="text-card-title text-ink-900">
                  {selectedStaff.name}
                </h3>
                <p className="text-sm text-ink-500">
                  {selectedStaff.department} • {selectedStaff.fte} FTE
                </p>
              </div>
            </div>
            <div className="flex space-x-2 mb-4">
              <button type="button" className={`px-3 py-1 text-sm rounded ${activeSemester === 'semester1' ? 'bg-navy-100 text-navy-800' : 'bg-ink-100'}`} onClick={() => setActiveSemester('semester1')}>
                Semester 1
              </button>
              <button type="button" className={`px-3 py-1 text-sm rounded ${activeSemester === 'semester2' ? 'bg-navy-100 text-navy-800' : 'bg-ink-100'}`} onClick={() => setActiveSemester('semester2')}>
                Semester 2
              </button>
            </div>
            <div>
              <h4 className="text-sm font-medium text-ink-700 mb-3">
                {activeSemester === 'semester1' ? 'Semester 1' : 'Semester 2'}{' '}
                Allowances
              </h4>
              {(() => {
            const allAllowances = getAllowancesForSemester(selectedStaff, activeSemester, workingYear);
            const semesterSpecific = selectedStaff[activeSemester]?.allowances || [];
            const bothAllowances = selectedStaff.both?.allowances || [];
            if (allAllowances.length === 0) {
              return <div className="text-center py-6 text-sm text-ink-500 bg-ink-50 rounded-md">
                      No allowances assigned for{' '}
                      {activeSemester === 'semester1' ? 'Semester 1' : 'Semester 2'}
                    </div>;
            }
            return <div className="space-y-2">
                    {semesterSpecific.length > 0 && <>
                        <div className="text-xs font-medium text-ink-500 uppercase tracking-wider">
                          Semester-specific
                        </div>
                        {semesterSpecific.map((allowance: any) => <div key={allowance.id} className="flex justify-between items-center p-3 bg-ink-50 rounded-md">
                            <div>
                              <div className="font-medium text-ink-900">
                                {allowance.name}
                              </div>
                              {allowance.notes && <div className="text-xs text-ink-500">
                                  {allowance.notes}
                                </div>}
                            </div>
                            <div className="text-sm font-medium text-ink-900 font-mono tabular-nums">
                              {allowance.minutes} minutes
                            </div>
                          </div>)}
                      </>}
                    {bothAllowances.length > 0 && <>
                        <div className="text-xs font-medium text-ink-500 uppercase tracking-wider mt-4">
                          Applies to both semesters
                        </div>
                        {bothAllowances.map((allowance: any) => <div key={allowance.id} className="flex justify-between items-center p-3 bg-navy-50 rounded-md border border-navy-200">
                            <div>
                              <div className="font-medium text-ink-900">
                                {allowance.name}
                              </div>
                              {allowance.notes && <div className="text-xs text-ink-500">
                                  {allowance.notes}
                                </div>}
                            </div>
                            <div className="text-sm font-medium text-navy-700 font-mono tabular-nums">
                              {allowance.minutes} minutes
                            </div>
                          </div>)}
                      </>}
                    <div className="flex justify-between pt-2 px-2 border-t">
                      <div className="text-sm font-medium text-ink-700">
                        Total Allowance:
                      </div>
                      <div className="text-sm font-medium text-navy-600 font-mono tabular-nums">
                        {calculateTotalAllowanceMinutes(selectedStaff, activeSemester, workingYear)}{' '}
                        minutes
                      </div>
                    </div>
                  </div>;
          })()}
            </div>
            <div className="bg-navy-50 p-4 rounded-md mt-4">
              <div className="flex">
                <Info className="h-5 w-5 text-navy-400 mr-2 flex-shrink-0" />
                <div className="text-sm text-navy-700">
                  <p>
                    Allowances reduce the staff member's teaching load by the
                    specified number of minutes.
                  </p>
                  <p className="mt-1">
                    Current reduction:{' '}
                    {calculateTotalAllowanceMinutes(selectedStaff, activeSemester, workingYear)}{' '}
                    minutes
                  </p>
                  {selectedStaff.both?.allowances?.length > 0 && <p className="mt-1 font-medium">
                      Note: Purple-highlighted allowances apply to both
                      semesters.
                    </p>}
                </div>
              </div>
            </div>
          </div>}
      </Modal>
    </div>;
};
export default StaffDirectory;